import { Button } from "@/components/ui/Button";
import { tagFullPath } from "@/lib/tagHierarchy";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import { useLongPress } from "../../hooks/useLongPress";
import {
  compareTagsPinnedFirst,
  usePinTag,
  useRenameTag,
  useTags,
} from "../../hooks/useTags";
import { Sentry } from "../../lib/sentry";
import { tagColorClassName, tagColorStyle } from "../../lib/tagColors";
import { TagIcon, TagTitle } from "../../lib/tagIcons";
import { useUIStore } from "../../store/ui";
import type { Tag as TagType } from "../../types";
import { TagEditor } from "../TagEditor";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/Input";
import { SidebarViewsSection } from "./SidebarViewsSection";

/* ── NavItem ─────────────────────────────────────────────── */

interface NavItemProps {
  label: string;
  icon?: React.ReactNode;
  href: string;
  onClick?: () => void;
  active?: boolean;
  indent?: boolean;
}

export function NavItem({
  label,
  icon,
  href,
  onClick,
  active,
  indent,
}: NavItemProps) {
  return (
    <Link
      to={href}
      onClick={onClick}
      className={[
        "flex w-full h-9 items-center gap-2 text-left transition-all duration-150 no-underline",
        "text-sm font-normal px-2.5 rounded-full cursor-pointer tracking-tight",
        indent ? "ml-2 !w-[calc(100%-8px)]" : "",
        active
          ? "bg-active text-accent-text"
          : "bg-transparent text-ink-secondary hover:bg-hover",
      ].join(" ")}
      title={label}
    >
      {icon && (
        <span
          className={`flex-shrink-0 flex items-center ${active ? "text-accent" : "text-muted"}`}
        >
          {icon}
        </span>
      )}
      <span className="truncate">{label}</span>
    </Link>
  );
}

/* ── TagNavItem ───────────────────────────────────────────── */

interface TagNavItemProps {
  tag: TagType;
  active: boolean;
  href: string;
  indent?: boolean;
  depth?: number;
  onClick?: () => void;
  onEditAppearance?: () => void;
  onRename?: () => void;
  onTogglePin?: () => void;
  hasChildren?: boolean;
}

function TagNavItem({
  tag,
  active,
  href,
  indent,
  depth = 0,
  onClick,
  onEditAppearance,
  onRename,
  onTogglePin,
  hasChildren,
}: TagNavItemProps) {
  const { t } = useTranslation();
  const indentLevel = Math.min(depth > 0 ? depth : indent ? 1 : 0, 2);
  const hasMenu = Boolean(onEditAppearance || onRename || onTogglePin);
  // Phones have no hover to reveal the options button: a long press on the
  // row opens the same menu, like the native sidebar's context menu.
  const [menuOpen, setMenuOpen] = useState(false);
  const { didLongPress, pressing: _pressing, ...longPress } = useLongPress(
    useCallback(() => setMenuOpen(true), []),
  );
  const tagIconClassName = active
    ? "text-accent"
    : tag.color
      ? (tagColorClassName(tag.color) ?? "")
      : "text-muted";
  const tagIconStyle = active ? undefined : tagColorStyle(tag.color);

  return (
    <div
      className="group/tag flex items-center w-full"
      style={
        // 16 per level, like the native sidebar.
        indentLevel > 0 ? { paddingLeft: `${indentLevel * 16}px` } : undefined
      }
    >
      <Link
        to={href}
        onClick={(e) => {
          // A long press just opened the menu: do not also navigate.
          if (didLongPress.current) {
            didLongPress.current = false;
            e.preventDefault();
            return;
          }
          onClick?.();
        }}
        {...(hasMenu ? longPress : undefined)}
        className={[
          "select-none [-webkit-touch-callout:none]",
          "flex flex-1 items-center gap-2 text-left min-w-0 transition-all duration-150",
          "text-sm font-normal px-2.5 rounded-full tracking-tight w-full h-9 cursor-pointer no-underline",
          active
            ? "border-l-accent bg-active text-accent-text"
            : `border-l-transparent text-ink-secondary hover:bg-hover ${menuOpen ? "bg-hover" : "bg-transparent"}`,
        ].join(" ")}
        title={tag.name}
      >
        <span
          className={`flex-shrink-0 flex items-center ${tagIconClassName}`}
          style={tagIconStyle}
        >
          {tag.icon ? (
            <TagIcon name={tag.icon} size={15} />
          ) : (
            <Icon name="tag" size={15} />
          )}
        </span>
        <span className="truncate min-w-0 text-left flex-1">{tag.name}</span>
        {tag.isPinned && (
          <Icon name="pin"
            size={11}
            className="flex-shrink-0 text-muted"
          />
        )}
        <div className="flex items-center -mr-1.5">
          {hasMenu && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-7 w-0 md:w-7 overflow-hidden pointer-events-none md:pointer-events-auto flex items-center justify-center rounded-full opacity-0 md:group-hover/tag:opacity-100 md:focus-visible:opacity-100 md:data-[state=open]:opacity-100 text-ink hover:bg-hover cursor-pointer bg-transparent border-none p-0"
                    title={t("sidebar.tagOptions")}
                  >
                    <Icon name="more" size={13} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  {onTogglePin && (
                    <DropdownMenuItem onSelect={onTogglePin}>
                      {tag.isPinned ? <Icon name="unpin" size={15} /> : <Icon name="pin" size={15} />}
                      {tag.isPinned
                        ? t("tagActions.unpin")
                        : t("tagActions.pin")}
                    </DropdownMenuItem>
                  )}
                  {onRename && (
                    <DropdownMenuItem onSelect={onRename}>
                      <Icon name="edit" size={15} />
                      {t("tagActions.rename")}
                    </DropdownMenuItem>
                  )}
                  {onEditAppearance && (
                    <DropdownMenuItem onSelect={onEditAppearance}>
                      <Icon name="palette" size={15} />
                      {t("tagActions.editAppearance")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          )}
          {hasChildren && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <AccordionTrigger className="h-7 w-7 flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent text-ink hover:bg-hover cursor-pointer bg-transparent">
                <Icon name="chevron-right" size={13} />
              </AccordionTrigger>
            </span>
          )}
        </div>
      </Link>
    </div>
  );
}

/* ── TagRenameDialog ──────────────────────────────────────── */

export function TagRenameDialog({
  tag,
  allTags,
  onClose,
}: {
  tag: TagType;
  allTags: TagType[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const renameTag = useRenameTag();
  const [value, setValue] = useState(tag.name);
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(
    () => new Map(allTags.map((item) => [item.id, item])),
    [allTags],
  );
  const parent = tag.parentId ? byId.get(tag.parentId) : undefined;
  const parentPrefix = parent ? `#${tagFullPath(parent, byId)}/` : "#";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await renameTag.mutate({ id: tag.id, name: value });
      if (result.ok) {
        toast.success(t("tagActions.renamed"));
        onClose();
      } else {
        setError(
          result.error === "conflict"
            ? t("tagActions.nameInUse")
            : t("tagActions.invalidName"),
        );
      }
    } catch (err) {
      Sentry.captureException(err);
      toast.error(t("tagActions.renameFailed"));
    }
  };

  return (
    <DialogContent className="max-w-xs">
      <DialogHeader>
        <DialogTitle>
          <Icon name="edit" size={14} />
          {t("tagActions.renameTitle")}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-3">
        <div className="flex items-center gap-1.5 min-w-0">
          {parentPrefix.length > 1 ? (
            <span className="text-sm text-muted shrink-0 font-chrome max-w-[40%] truncate">
              {parentPrefix}
            </span>
          ) : (
            <span className="text-sm text-muted shrink-0 font-chrome">#</span>
          )}
          <Input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            className="flex-1 min-w-0"
          />
        </div>
        {error && <p className="text-xs text-danger m-0">{error}</p>}
        <p className="text-xs text-muted m-0 leading-relaxed">
          {t("tagActions.renameHint")}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={renameTag.isPending || !value.trim()}
          >
            {t("tagActions.renameConfirm")}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}

/* ── Section label ───────────────────────────────────────── */

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold tracking-[0.11em] uppercase text-muted px-2.5 mb-1 mt-0 font-chrome">
      {children}
    </p>
  );
}

import { Icon } from "@/components/ui/Icon";

/* ── Sidebar ─────────────────────────────────────────────── */

export function Sidebar({
  onNavigate,
  drawer,
}: { onNavigate?: () => void; drawer?: boolean } = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id: noteId } = useParams<{ id?: string }>();
  const tags = useTags();
  const { openSettings, openTagIds, setOpenTagIds } = useUIStore();

  // Build a sidebar href that preserves the current note path
  const basePath = noteId ? `/app/note/${noteId}` : "/app";
  const buildHref = (params?: string) =>
    params ? `${basePath}?${params}` : basePath;

  const [editingTag, setEditingTag] = useState<TagType | null>(null);
  const [renamingTag, setRenamingTag] = useState<TagType | null>(null);
  const pinTag = usePinTag();

  const handleSidebarClose = useCallback(() => {
    onNavigate?.();
  }, [onNavigate]);

  const currentTag = searchParams.get("tag");

  const handleTogglePin = async (tag: TagType) => {
    try {
      await pinTag.mutate({ id: tag.id, isPinned: !tag.isPinned });
    } catch (err) {
      Sentry.captureException(err);
      toast.error(t("tagActions.pinFailed"));
    }
  };

  const isAppPath =
    location.pathname === "/app" || location.pathname.startsWith("/app/note/");

  const tagList = tags;

  // When openTagIds is null (no localStorage value), default to all parent tags expanded
  const parentIds = useMemo(
    () =>
      tagList
        .filter((t) => tagList.some((c) => c.parentId === t.id))
        .map((t) => t.id),
    [tagList],
  );
  const effectiveOpenTagIds = openTagIds ?? parentIds;

  // Recursive tag tree renderer — supports arbitrary depth.
  // Pinned tags sort first at every level, then alphabetical.
  const renderTagTree = (tag: TagType, depth = 0) => {
    const children = tagList
      .filter((t) => t.parentId === tag.id)
      .sort(compareTagsPinnedFirst);
    const hasChildren = children.length > 0;

    const itemProps = {
      tag,
      active: isAppPath && currentTag === tag.id,
      href: buildHref(`tag=${tag.id}`),
      depth,
      onClick: handleSidebarClose,
      onEditAppearance: () => setEditingTag(tag),
      onRename: () => setRenamingTag(tag),
      onTogglePin: () => void handleTogglePin(tag),
    };

    if (!hasChildren) {
      return <TagNavItem key={tag.id} {...itemProps} />;
    }

    return (
      <AccordionItem key={tag.id} value={tag.id}>
        <TagNavItem {...itemProps} hasChildren />
        <AccordionContent>
          {children.map((child) => renderTagTree(child, depth + 1))}
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div
      className="flex h-full flex-col overflow-y-auto gap-4 py-2.5 px-2 font-chrome"
    >
      {/* Smart views */}
      <SidebarViewsSection onLinkClick={handleSidebarClose} showHeader={!drawer} />

      {/* Tags */}
      {tagList.length > 0 && (
        <div>
          <SectionLabel>{t("sidebar.tags")}</SectionLabel>
          <Dialog
            open={editingTag !== null}
            onOpenChange={(o) => {
              if (!o) setEditingTag(null);
            }}
          >
            {editingTag && (
              <DialogContent className="max-w-xs p-0">
                <DialogHeader className="px-3 py-2 border-b border-border-light">
                  <DialogTitle className="text-xs font-semibold text-ink-secondary">
                    {/* The live row, so the title follows the icon and colour being picked. */}
                    <TagTitle
                      tag={tags.find((t) => t.id === editingTag.id) ?? editingTag}
                      path={tagFullPath(
                        editingTag,
                        new Map(tags.map((t) => [t.id, t])),
                      )}
                    />
                  </DialogTitle>
                </DialogHeader>
                <TagEditor tag={editingTag} />
              </DialogContent>
            )}
          </Dialog>
          <Dialog
            open={renamingTag !== null}
            onOpenChange={(o) => {
              if (!o) setRenamingTag(null);
            }}
          >
            {renamingTag && (
              <TagRenameDialog
                tag={renamingTag}
                allTags={tags}
                onClose={() => setRenamingTag(null)}
              />
            )}
          </Dialog>
          <Accordion
            type="multiple"
            value={effectiveOpenTagIds}
            onValueChange={setOpenTagIds}
            className="flex flex-col"
          >
            {tagList
              .filter((t) => !t.parentId)
              .sort(compareTagsPinnedFirst)
              .map((tag) => renderTagTree(tag))}
          </Accordion>
        </div>
      )}

      {/* Settings pill, bottom-right like the native sidebar's. */}
      <div className="mt-auto sticky bottom-0 flex justify-end pt-2 pb-1 bg-inherit">
        <Button
          variant={drawer ? "outline" : "secondary"}
          // The sheet opens over the drawer; closing it lands back here.
          onClick={() => openSettings()}
          // Phones: the same pill as the drawer header's Edit.
          className={
            drawer
              ? "gap-2 px-4 text-[17px] font-semibold"
              : "h-8 gap-1.5 px-3.5 text-[13px] font-semibold text-ink border border-border bg-canvas hover:bg-hover"
          }
        >
          <Icon name="sliders" size={drawer ? 18 : 14} />
          {t("settings.title")}
        </Button>
      </div>
    </div>
  );
}
